import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import ImageUpload, { ImageUploadResult } from './ImageUpload';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  baseUrl?: string;
  uploadPath?: string; // backend upload endpoint, e.g. 'disk/upload'
  fillHeight?: boolean; // stretch the editor to fill its parent's height and scroll internally
}

const CALLOUT_ICONS: Record<string, string> = {
  info: 'ℹ️', warn: '⚠️', tip: '💡', success: '✅', danger: '❌',
};

// Text-color palette (8 per row → 3 rows)
const TEXT_COLORS = [
  '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#ffffff', '#ef4444', '#f97316',
  '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#2563eb', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#7c2d12',
];

// Strip tags → plain text (for the word counter)
const plainText = (html: string) => {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return d.textContent || '';
};

// ─── inline styles (portal CSS vars) ──────────────────────────────────────────
const EDITOR_STYLES = `
  .re-editor { outline:none; font-size:15px; line-height:1.75; color:var(--text-color); }
  .re-editor:empty::before { content:attr(data-placeholder); color:var(--text-color-calendar-week); }
  .re-editor h1 { font-size:26px;font-weight:900;margin:18px 0 10px;letter-spacing:-.02em; }
  .re-editor h2 { font-size:20px;font-weight:800;margin:16px 0 8px; }
  .re-editor h3 { font-size:16px;font-weight:700;margin:12px 0 6px; }
  .re-editor p  { margin-bottom:10px; }
  .re-editor ul,.re-editor ol { margin:0 0 12px 22px; }
  .re-editor li { margin-bottom:4px; }
  .re-editor blockquote { border-left:3px solid #2563eb;padding:10px 18px;background:rgba(37,99,235,.05);border-radius:0 8px 8px 0;margin:14px 0;color:var(--text-color-calendar-week);font-style:italic; }
  .re-editor a   { color:#2563eb;text-decoration:underline; }
  .re-editor code { background:rgba(37,99,235,.08);color:#2563eb;border-radius:4px;padding:1px 6px;font-family:monospace;font-size:.88em; }
  .re-editor pre  { background:rgba(0,0,0,.05);border:1px solid var(--border-input);border-radius:10px;padding:16px;overflow-x:auto;margin:14px 0; }
  .re-editor pre code { background:none;color:var(--text-color);padding:0;font-size:13px; }
  .re-editor mark { background:rgba(251,191,36,.35);border-radius:3px;padding:0 2px; }
  .re-editor hr   { border:none;border-top:2px solid var(--border-input);margin:20px 0; }
  .re-editor table { width:100%;border-collapse:collapse;margin:14px 0;border-radius:8px;overflow:hidden; }
  .re-editor th   { background:rgba(37,99,235,.07);font-size:12px;font-weight:700;padding:10px 14px;text-align:left;border-bottom:2px solid var(--border-input); }
  .re-editor td   { padding:9px 14px;border-bottom:1px solid var(--border-input);font-size:13px;color:var(--text-color-calendar-week); }
  .re-editor tr:last-child td { border-bottom:none; }
  .re-editor .img-block { margin:16px 0; }
  .re-editor .img-block img { border-radius:10px;display:block;max-width:100%; }
  .re-editor .img-caption { font-size:12px;color:var(--text-color-calendar-week);text-align:center;margin-top:5px; }
  .re-editor .video-block { position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:10px;margin:16px 0; }
  .re-editor .video-block iframe { position:absolute;top:0;left:0;width:100%;height:100%;border:none; }
  .re-editor .callout { display:flex;gap:12px;padding:13px 16px;border-radius:10px;margin:12px 0;border:1px solid; }
  .re-editor .callout.info    { background:rgba(37,99,235,.06);border-color:rgba(37,99,235,.18); }
  .re-editor .callout.warn    { background:rgba(217,119,6,.06);border-color:rgba(217,119,6,.22); }
  .re-editor .callout.tip     { background:rgba(5,150,105,.06);border-color:rgba(5,150,105,.2); }
  .re-editor .callout.success { background:rgba(5,150,105,.08);border-color:rgba(5,150,105,.3); }
  .re-editor .callout.danger  { background:rgba(220,38,38,.06);border-color:rgba(220,38,38,.2); }
  .re-editor .callout-icon { font-size:17px;flex-shrink:0;margin-top:1px; }
  .re-editor kbd { display:inline-block;background:var(--btn-user-form);border:1px solid var(--border-input);border-bottom-width:2px;border-radius:5px;padding:1px 6px;font-size:.8em;font-family:monospace;color:var(--text-color-calendar-week); }
`;

// ─── Toolbar button ────────────────────────────────────────────────────────────
const Btn: React.FC<{
  title?: string; onClick: () => void; active?: boolean;
  children: React.ReactNode; style?: React.CSSProperties;
}> = ({ title, onClick, active, children, style }) => (
  <button type="button" title={title} onClick={onClick}
    onMouseDown={e => e.preventDefault()}
    style={{
      minWidth: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
      padding: '0 5px', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
      background: active ? 'rgba(37,99,235,.12)' : 'transparent',
      color: active ? '#2563eb' : 'var(--text-color-calendar-week)',
      transition: 'all .12s', whiteSpace: 'nowrap', ...style,
    }}
    onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(37,99,235,.07)'; }}
    onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
  >{children}</button>
);

const Sep = () => (
  <div style={{ width: 1, height: 20, background: 'var(--border-input)', margin: '0 3px', flexShrink: 0 }} />
);

// ─── Main component ────────────────────────────────────────────────────────────
const RichEditor: React.FC<Props> = ({
  value, onChange, placeholder = 'Start writing…', minHeight = 320, baseUrl = '', uploadPath = 'disk/upload', fillHeight = false,
}) => {
  const editorRef   = useRef<HTMLDivElement>(null);
  const savedRange  = useRef<Range | null>(null);
  const [dragOver, setDragOver]     = useState(false);
  const [htmlMode, setHtmlMode]     = useState(false);
  const [htmlSource, setHtmlSource] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Modals
  const [imgModal, setImgModal]   = useState(false); // kept for URL-tab fallback
  const [ytModal, setYtModal]     = useState(false);
  const [tblModal, setTblModal]   = useState(false);

  // Image state
  const [imgUploadOpen, setImgUploadOpen] = useState(false);
  const [imgUploading,  setImgUploading]  = useState(false);

  // Image alignment toolbar
  const [selImg, setSelImg]           = useState<HTMLImageElement | null>(null);
  const [imgToolbarPos, setImgToolbarPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!selImg) return;
    const hide = () => setSelImg(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [selImg]);

  // YouTube state
  const [ytUrl, setYtUrl]         = useState('');
  const [ytCaption, setYtCaption] = useState('');
  const [ytId, setYtId]           = useState('');

  // Table state
  const [tblRows, setTblRows]     = useState(0);
  const [tblCols, setTblCols]     = useState(0);
  const [tblHeader, setTblHeader] = useState(true);

  // Colour
  const [textColor, setTextColor] = useState('#2563eb');
  const [colorOpen, setColorOpen] = useState(false);

  // Which formatting commands are active for the current selection
  const [fmt, setFmt] = useState<Record<string, boolean>>({});

  // Inject styles once
  useEffect(() => {
    if (!document.getElementById('re-styles')) {
      const s = document.createElement('style');
      s.id = 're-styles'; s.textContent = EDITOR_STYLES;
      document.head.appendChild(s);
    }
  }, []);

  // Sync value → editor (only on mount / external change)
  useEffect(() => {
    if (editorRef.current && !htmlMode) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value, htmlMode]);

  // Read which commands are currently "on" for the caret/selection so the
  // matching toolbar buttons can light up (and un-light when toggled off).
  const refreshFormats = useCallback(() => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return;
    const cmds = [
      'bold', 'italic', 'underline', 'strikeThrough',
      'insertUnorderedList', 'insertOrderedList',
      'justifyLeft', 'justifyCenter', 'justifyRight',
    ];
    const next: Record<string, boolean> = {};
    for (const c of cmds) {
      try { next[c] = document.queryCommandState(c); } catch { next[c] = false; }
    }
    setFmt(next);
  }, []);

  // Keep button states in sync as the caret moves around the editor.
  useEffect(() => {
    const handler = () => refreshFormats();
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [refreshFormats]);

  // Run a formatting command. execCommand only affects the editor when it is
  // focused AND has a live selection inside it — so we focus first, restore a
  // saved caret (or drop one at the end) if the selection drifted outside, then
  // run the command and push the new HTML up.
  const exec = useCallback((cmd: string, val?: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    const selInside = !!sel && sel.rangeCount > 0 && el.contains(sel.anchorNode);
    if (!selInside) {
      sel?.removeAllRanges();
      if (savedRange.current) {
        sel?.addRange(savedRange.current);
      } else {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel?.addRange(range);
      }
    }
    savedRange.current = null;
    document.execCommand(cmd, false, val ?? undefined);
    onChange(el.innerHTML);
    refreshFormats();
  }, [onChange, refreshFormats]);

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  // Remember where the caret is before a modal steals focus, so we can
  // insert back at the right spot when the modal closes.
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRange.current = range.cloneRange();
        return;
      }
    }
    savedRange.current = null;
  }, []);

  // Restore the saved caret (or fall back to the end of the editor), then
  // insert HTML. execCommand only works when the editable element is focused
  // with a live selection — hence the focus + range restore before it runs.
  const insertHtml = useCallback((html: string) => {
    const el = editorRef.current;
    if (!el) return;
    if (!savedRange.current) saveSelection(); // toolbar inserts: grab the live caret
    el.focus();
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      if (savedRange.current) {
        sel.addRange(savedRange.current);
      } else {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.addRange(range);
      }
    }
    const ok = document.execCommand('insertHTML', false, html);
    if (!ok) el.innerHTML += html; // fallback for browsers that reject insertHTML
    savedRange.current = null;
    onChange(el.innerHTML);
  }, [onChange, saveSelection]);

  // Current HTML regardless of which mode we're in.
  const getHtml = useCallback(
    () => (htmlMode ? htmlSource : (editorRef.current?.innerHTML ?? value)),
    [htmlMode, htmlSource, value],
  );

  // ── Text colour ────────────────────────────────────────────────────────────────
  const applyColor = useCallback((color: string) => {
    setTextColor(color);
    setColorOpen(false);
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      if (savedRange.current) sel.addRange(savedRange.current);
    }
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('foreColor', false, color);
    savedRange.current = null;
    onChange(el.innerHTML);
  }, [onChange]);

  // ── HTML mode toggle ─────────────────────────────────────────────────────────
  const toggleHtml = () => {
    if (!htmlMode) {
      // entering source mode — capture the live DOM
      setHtmlSource(editorRef.current?.innerHTML ?? value ?? '');
      setHtmlMode(true);
    } else {
      // leaving source mode — push edits up, then paint the editor once it remounts
      const next = htmlSource;
      setHtmlMode(false);
      onChange(next);
      requestAnimationFrame(() => {
        if (editorRef.current && editorRef.current.innerHTML !== next) {
          editorRef.current.innerHTML = next;
        }
      });
    }
  };

  // ── Image upload via ImageUpload component ────────────────────────────────────
  const handleImageComplete = async ({ file, fileName }: ImageUploadResult) => {
    if (!uploadPath) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, fileName);
      const { data } = await axios.post(uploadPath, fd);
      const url = (data.url || data.path || '').replace(/^\//, '');
      const fullUrl = `${baseUrl ?? ''}${url}`;
      const html = `<div class="img-block" style="width:100%"><img src="${fullUrl}" alt="${fileName}" style="border-radius:10px;width:100%;display:block;"/></div><p><br></p>`;
      insertHtml(html);
    } catch { /* handled globally */ }
    setImgUploading(false);
  };

  // ── Image alignment/size controls ────────────────────────────────────────────
  const applyImgAlign = useCallback((align: 'left' | 'center' | 'right') => {
    if (!selImg) return;
    const block = selImg.closest('.img-block') as HTMLElement | null;
    if (!block) return;
    block.style.display = 'flex';
    block.style.justifyContent = align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'flex-end';
    onChange(editorRef.current?.innerHTML || '');
  }, [selImg, onChange]);

  const applyImgSize = useCallback((pct: number) => {
    if (!selImg) return;
    selImg.style.width = `${pct}%`;
    // Reposition toolbar since image size changed
    requestAnimationFrame(() => {
      const r = selImg.getBoundingClientRect();
      setImgToolbarPos({ top: r.top - 44, left: r.left, width: r.width });
    });
    onChange(editorRef.current?.innerHTML || '');
  }, [selImg, onChange]);

  const handleEditorClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      const r = img.getBoundingClientRect();
      setSelImg(img);
      setImgToolbarPos({ top: r.top - 44, left: r.left, width: r.width });
    } else {
      setSelImg(null);
    }
  }, []);

  // ── YouTube ───────────────────────────────────────────────────────────────────
  const extractYTId = (raw: string) => {
    const val = (raw || '').trim();
    if (!val) return null;
    // Bare 11-char video id
    if (/^[a-zA-Z0-9_-]{11}$/.test(val)) return val;
    // Any known YouTube URL shape: watch?v=, youtu.be/, /embed/, /shorts/, /live/, /v/, /e/
    const m = val.match(/(?:v=|\/(?:embed|shorts|live|v|e)\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (m) return m[1];
    // Fallback: first 11-char token that looks like an id (e.g. odd query layouts)
    const f = val.match(/[a-zA-Z0-9_-]{11}/);
    return f ? f[0] : null;
  };

  const insertYoutube = () => {
    if (!ytId) return;
    const html = `<div class="video-block"><iframe src="https://www.youtube.com/embed/${ytId}?controls=1&modestbranding=1" allowfullscreen></iframe></div>${ytCaption ? `<p class="img-caption" style="text-align:center">${ytCaption}</p>` : ''}<p><br></p>`;
    insertHtml(html);
    setYtModal(false); setYtUrl(''); setYtCaption(''); setYtId('');
  };

  // ── Table ─────────────────────────────────────────────────────────────────────
  const insertTable = (r = tblRows || 2, c = tblCols || 3) => {
    let html = '<table>';
    if (tblHeader) { html += '<tr>'; for (let i = 0; i < c; i++) html += `<th>Header ${i + 1}</th>`; html += '</tr>'; }
    for (let i = 0; i < (tblHeader ? r - 1 : r); i++) { html += '<tr>'; for (let j = 0; j < c; j++) html += '<td>Cell</td>'; html += '</tr>'; }
    html += '</table><p><br></p>';
    insertHtml(html);
    setTblModal(false);
  };

  // ── Callouts ─────────────────────────────────────────────────────────────────
  const insertCallout = (type: string) => {
    insertHtml(`<div class="callout ${type}"><span class="callout-icon">${CALLOUT_ICONS[type]}</span><div>Your note here…</div></div><p><br></p>`);
  };

  // ── Styles ────────────────────────────────────────────────────────────────────
  const tb: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 2, padding: '7px 10px',
    borderBottom: '1px solid var(--border-input)', background: 'var(--bg-top)',
    flexWrap: 'wrap', rowGap: 4,
  };
  const modalOverlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)',
  };
  const modalBox: React.CSSProperties = {
    background: 'var(--bg-form)', border: '1px solid var(--border-input)',
    borderRadius: 16, width: 440, boxShadow: '0 24px 60px rgba(0,0,0,.25)', overflow: 'hidden',
  };
  const mHead: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '13px 18px', borderBottom: '1px solid var(--border-input)', background: 'var(--bg-top)',
  };
  const mBody: React.CSSProperties = { padding: '16px 18px' };
  const mFoot: React.CSSProperties = {
    padding: '11px 18px', borderTop: '1px solid var(--border-input)',
    display: 'flex', gap: 8, justifyContent: 'flex-end', background: 'var(--bg-top)',
  };
  const inpStyle: React.CSSProperties = {
    width: '100%', padding: '8px 11px', border: '1.5px solid var(--border-input)',
    borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-color)',
    fontSize: 13, fontFamily: 'inherit', outline: 'none',
  };
  const inpLbl: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, color: 'var(--text-color-calendar-week)',
    marginBottom: 4, letterSpacing: '.04em', textTransform: 'uppercase', display: 'block',
  };
  const primaryBtn: React.CSSProperties = {
    padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white',
    fontSize: 12, fontWeight: 700,
  };
  const ghostBtn: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-input)',
    background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  };

  return (
    <>
      <div style={{ border: '1px solid var(--border-input)', borderRadius: 12, overflow: 'hidden',
        ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' } : {}) }}>

        {/* ── TOOLBAR ── */}
        <div style={{ ...tb, flexShrink: 0 }}>
          {/* Style select */}
          <select
            onMouseDown={() => saveSelection()}
            onChange={e => {
              const v = e.target.value;
              (e.target as HTMLSelectElement).selectedIndex = 0;
              const el = editorRef.current;
              if (!el) return;
              el.focus();
              const sel = window.getSelection();
              if (sel && savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
              document.execCommand('formatBlock', false, v);
              savedRange.current = null;
              onChange(el.innerHTML);
            }}
            style={{ height: 27, padding: '0 6px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}>
            <option value="" disabled selected>Style</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="p">Paragraph</option>
            <option value="blockquote">Quote</option>
            <option value="pre">Code Block</option>
          </select>

          <Sep />
          <Btn title="Bold" active={fmt.bold} onClick={() => exec('bold')}><b>B</b></Btn>
          <Btn title="Italic" active={fmt.italic} onClick={() => exec('italic')}><i style={{ fontStyle: 'italic' }}>I</i></Btn>
          <Btn title="Underline" active={fmt.underline} onClick={() => exec('underline')}><u>U</u></Btn>
          <Btn title="Strike" active={fmt.strikeThrough} onClick={() => exec('strikeThrough')}><s>S</s></Btn>
          <Btn title="Inline code" onClick={() => { const sel = window.getSelection()?.toString() || 'code'; exec('insertHTML', `<code>${sel}</code>`); }} style={{ fontFamily: 'monospace', fontSize: 11 }}>&lt;/&gt;</Btn>

          <Sep />
          {/* Text color — custom palette popover */}
          <div style={{ position: 'relative' }}>
            {/* onMouseDown preventDefault keeps the editor selection alive when the button is clicked */}
            <button type="button" title="Text color"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { if (!colorOpen) saveSelection(); setColorOpen(o => !o); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: 'pointer', background: colorOpen ? 'rgba(37,99,235,.1)' : 'transparent', border: 'none', borderRadius: 6, padding: '3px 5px', height: 28 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-color-calendar-week)', lineHeight: 1 }}>A</span>
              <div style={{ width: 16, height: 3, borderRadius: 2, background: textColor }} />
            </button>
            {colorOpen && (
              <>
                <div onClick={() => setColorOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 51, background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 10, padding: 10, boxShadow: '0 12px 32px rgba(0,0,0,.2)', width: 198 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 18px)', gap: 6 }}>
                    {TEXT_COLORS.map(c => (
                      <button key={c} type="button" title={c}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => applyColor(c)}
                        style={{ width: 18, height: 18, borderRadius: 5, cursor: 'pointer', background: c, padding: 0,
                          border: c.toLowerCase() === '#ffffff' ? '1px solid var(--border-input)' : '1px solid transparent',
                          outline: textColor.toLowerCase() === c.toLowerCase() ? '2px solid #2563eb' : 'none', outlineOffset: 1 }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-input)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-color-calendar-week)', cursor: 'pointer' }}>
                      <input type="color" value={textColor} onChange={e => applyColor(e.target.value)}
                        style={{ width: 26, height: 22, padding: 0, border: '1px solid var(--border-input)', borderRadius: 5, background: 'transparent', cursor: 'pointer' }} />
                      Custom
                    </label>
                    <button type="button" onMouseDown={e => e.preventDefault()}
                      onClick={() => applyColor(editorRef.current ? getComputedStyle(editorRef.current).color : '#000000')}
                      style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar-week)', background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                      Reset
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Btn title="Highlight" onClick={() => exec('hiliteColor', '#FDE68A')} style={{ fontSize: 13 }}>🖊</Btn>
          <Btn title="Remove highlight" onClick={() => exec('hiliteColor', 'transparent')} style={{ fontSize: 9 }}>✕hl</Btn>

          <Sep />
          <Btn title="Bullet list" active={fmt.insertUnorderedList} onClick={() => exec('insertUnorderedList')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/>
              <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/>
              <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/>
              <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/>
            </svg>
          </Btn>
          <Btn title="Numbered list" active={fmt.insertOrderedList} onClick={() => exec('insertOrderedList')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="10" y1="6" x2="20" y2="6"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="10" y1="18" x2="20" y2="18"/>
            </svg>
          </Btn>
          <Btn title="Indent" onClick={() => exec('indent')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="11" y1="12" x2="21" y2="12"/><line x1="11" y1="18" x2="21" y2="18"/>
              <polyline points="3,9 7,12 3,15"/>
            </svg>
          </Btn>
          <Btn title="Outdent" onClick={() => exec('outdent')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="11" y1="12" x2="21" y2="12"/><line x1="11" y1="18" x2="21" y2="18"/>
              <polyline points="7,9 3,12 7,15"/>
            </svg>
          </Btn>

          <Sep />
          <Btn title="Align left" active={fmt.justifyLeft} onClick={() => exec('justifyLeft')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="14" y2="12"/><line x1="3" y1="18" x2="17" y2="18"/>
            </svg>
          </Btn>
          <Btn title="Align center" active={fmt.justifyCenter} onClick={() => exec('justifyCenter')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
          </Btn>
          <Btn title="Align right" active={fmt.justifyRight} onClick={() => exec('justifyRight')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="7" y1="18" x2="21" y2="18"/>
            </svg>
          </Btn>

          <Sep />
          <Btn title="Insert link" onClick={() => { const u = prompt('URL:', 'https://'); if (u) exec('createLink', u); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </Btn>
          <Btn title="Horizontal rule" onClick={() => exec('insertHTML', '<hr><p><br></p>')} style={{ fontSize: 10, letterSpacing: -1 }}>——</Btn>
          <Btn title="Keyboard shortcut" onClick={() => { const s = window.getSelection()?.toString() || 'Key'; exec('insertHTML', `<kbd>${s}</kbd>`); }} style={{ fontFamily: 'monospace', fontSize: 10, border: '1px solid var(--border-input)', borderRadius: 4, padding: '0 3px' }}>kbd</Btn>

          <Sep />
          {/* Image */}
          <Btn onClick={() => { saveSelection(); setImgUploadOpen(true); }} style={{ background: 'rgba(37,99,235,.08)', color: '#2563eb', padding: '0 9px', fontSize: 11, fontWeight: 700 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>
            Image
          </Btn>
          {/* YouTube */}
          <Btn onClick={() => { saveSelection(); setYtModal(true); }} style={{ background: 'rgba(220,38,38,.08)', color: '#dc2626', padding: '0 9px', fontSize: 11, fontWeight: 700 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
              <polygon points="9.75,15.02 15.5,12 9.75,8.98 9.75,15.02" fill="#dc2626" stroke="none"/>
            </svg>
            YouTube
          </Btn>
          {/* Table */}
          <Btn onClick={() => { saveSelection(); setTblModal(true); }} style={{ background: 'rgba(5,150,105,.08)', color: '#059669', padding: '0 9px', fontSize: 11, fontWeight: 700 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
            Table
          </Btn>

          <Sep />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', padding: '0 2px', alignSelf: 'center' }}>Note:</span>
          {Object.keys(CALLOUT_ICONS).map(type => (
            <Btn key={type} title={type} onClick={() => insertCallout(type)} style={{ fontSize: 13 }}>{CALLOUT_ICONS[type]}</Btn>
          ))}

          <Sep />
          <Btn title="Clear formatting" onClick={() => exec('removeFormat')} style={{ fontSize: 10 }}>Tx</Btn>

          {/* HTML mode */}
          <Btn onClick={toggleHtml} active={htmlMode} title="HTML source mode"
            style={{ marginLeft: 'auto', border: '1px solid var(--border-input)', padding: '0 9px', fontSize: 10, fontFamily: 'monospace', fontWeight: 800 }}>
            &lt;/&gt; HTML
          </Btn>
        </div>

        {/* ── EDITOR / HTML SOURCE ── */}
        {htmlMode ? (
          <textarea
            value={htmlSource}
            onChange={e => { setHtmlSource(e.target.value); onChange(e.target.value); }}
            style={{
              display: 'block', width: '100%', border: 'none', outline: 'none',
              fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: 13, lineHeight: 1.6,
              color: 'var(--text-color)', background: 'var(--btn-user-form)',
              padding: '20px 24px', boxSizing: 'border-box',
              ...(fillHeight ? { flex: 1, minHeight: 0, resize: 'none' } : { minHeight, resize: 'vertical' }),
            }}
          />
        ) : (
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="re-editor"
            data-placeholder={placeholder}
            onInput={handleInput}
            onClick={handleEditorClick}
            style={{ padding: '20px 28px', overflowY: 'auto',
              ...(fillHeight ? { flex: 1, minHeight: 0 } : { minHeight }) }}
          />
        )}

        {/* ── STATUS BAR ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 14px', borderTop: '1px solid var(--border-input)', background: 'var(--bg-top)', fontSize: 11, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>
          <span>{plainText(htmlMode ? htmlSource : value).trim().split(/\s+/).filter(Boolean).length} words</span>
          <button type="button" onClick={() => setPreviewOpen(true)}
            style={{ ...ghostBtn, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="16,18 22,12 16,6"/><polyline points="8,6 2,12 8,18"/></svg>
            View as portal page
          </button>
        </div>
      </div>

      {/* ── IMAGE ALIGNMENT TOOLBAR ── */}
      {selImg && createPortal(
        <div
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed',
            top: imgToolbarPos.top,
            left: imgToolbarPos.left,
            width: Math.max(imgToolbarPos.width, 220),
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            background: 'var(--bg-form)',
            border: '1px solid var(--border-input)',
            borderRadius: 8,
            padding: '4px 6px',
            boxShadow: '0 4px 16px rgba(0,0,0,.14)',
          }}>
          {/* Alignment */}
          {([
            { align: 'left',   title: 'Align left',   icon: '⬅' },
            { align: 'center', title: 'Center',        icon: '↔' },
            { align: 'right',  title: 'Align right',  icon: '➡' },
          ] as const).map(({ align, title, icon }) => {
            const block = selImg.closest('.img-block') as HTMLElement | null;
            const cur = block?.style.justifyContent;
            const isActive = (align === 'left' && (!cur || cur === 'flex-start'))
              || (align === 'center' && cur === 'center')
              || (align === 'right' && cur === 'flex-end');
            return (
              <button key={align} type="button" title={title}
                onClick={() => applyImgAlign(align)}
                style={{ minWidth: 28, height: 26, borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 13,
                  background: isActive ? 'rgba(37,99,235,.12)' : 'transparent',
                  color: isActive ? '#2563eb' : 'var(--text-color-calendar)' }}>
                {icon}
              </button>
            );
          })}
          <div style={{ width: 1, height: 18, background: 'var(--border-input)', margin: '0 3px' }} />
          {/* Width */}
          {[25, 50, 75, 100].map(pct => {
            const cur = parseInt(selImg.style.width || '100');
            return (
              <button key={pct} type="button" title={`Width ${pct}%`}
                onClick={() => applyImgSize(pct)}
                style={{ minWidth: 34, height: 26, borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: cur === pct ? 'rgba(37,99,235,.12)' : 'transparent',
                  color: cur === pct ? '#2563eb' : 'var(--text-color-calendar)' }}>
                {pct}%
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {/* ── IMAGE MODAL ── */}
      {/* ImageUpload universal component — replaces old imgModal */}
      <ImageUpload
        open={imgUploadOpen}
        onClose={() => setImgUploadOpen(false)}
        onComplete={handleImageComplete}
      />

      {/* ── YOUTUBE MODAL ── */}
      {ytModal && (
        <div style={modalOverlay} onClick={e => { if (e.target === e.currentTarget) setYtModal(false); }}>
          <div style={modalBox}>
            <div style={mHead}>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-color)' }}>Embed YouTube Video</span>
              <button type="button" onClick={() => setYtModal(false)} style={ghostBtn}>✕</button>
            </div>
            <div style={mBody}>
              <div style={{ marginBottom: 12 }}>
                <label style={inpLbl}>YouTube URL or Video ID</label>
                <input style={inpStyle} value={ytUrl} placeholder="https://youtube.com/watch?v=… or ID"
                  onChange={e => { setYtUrl(e.target.value); setYtId(extractYTId(e.target.value) || ''); }} />
              </div>
              {ytId && (
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 8, marginBottom: 12 }}>
                  <iframe src={`https://www.youtube.com/embed/${ytId}?modestbranding=1`}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allowFullScreen title="preview" />
                </div>
              )}
              <div>
                <label style={inpLbl}>Caption (optional)</label>
                <input style={inpStyle} value={ytCaption} placeholder="Video description…" onChange={e => setYtCaption(e.target.value)} />
              </div>
            </div>
            <div style={mFoot}>
              <button type="button" style={ghostBtn} onClick={() => setYtModal(false)}>Cancel</button>
              <button type="button" style={primaryBtn} onClick={insertYoutube} disabled={!ytId}>Embed Video</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TABLE MODAL ── */}
      {tblModal && (
        <div style={modalOverlay} onClick={e => { if (e.target === e.currentTarget) setTblModal(false); }}>
          <div style={modalBox}>
            <div style={mHead}>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-color)' }}>Insert Table</span>
              <button type="button" onClick={() => setTblModal(false)} style={ghostBtn}>✕</button>
            </div>
            <div style={mBody}>
              <p style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginBottom: 8 }}>Hover to select size:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 28px)', gap: 3, marginBottom: 8 }}>
                {Array.from({ length: 24 }, (_, i) => {
                  const r = Math.floor(i / 6) + 1; const c = (i % 6) + 1;
                  return (
                    <div key={i}
                      style={{ width: 28, height: 28, borderRadius: 4, cursor: 'pointer', border: '1px solid var(--border-input)', transition: 'all .1s',
                        background: r <= tblRows && c <= tblCols ? 'rgba(37,99,235,.15)' : 'var(--btn-user-form)',
                        borderColor: r <= tblRows && c <= tblCols ? 'rgba(37,99,235,.4)' : 'var(--border-input)',
                      }}
                      onMouseEnter={() => { setTblRows(r); setTblCols(c); }}
                      onClick={() => insertTable(r, c)}
                    />
                  );
                })}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-color)', marginBottom: 12, height: 16 }}>
                {tblRows > 0 && tblCols > 0 ? `${tblRows} rows × ${tblCols} columns` : ''}
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={tblHeader} onChange={e => setTblHeader(e.target.checked)} />
                Include header row
              </label>
            </div>
            <div style={mFoot}>
              <button type="button" style={ghostBtn} onClick={() => setTblModal(false)}>Cancel</button>
              <button type="button" style={primaryBtn} onClick={() => insertTable()} disabled={!tblRows}>Insert Table</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PORTAL PREVIEW MODAL ── */}
      {previewOpen && (
        <div style={modalOverlay} onClick={e => { if (e.target === e.currentTarget) setPreviewOpen(false); }}>
          <div style={{ ...modalBox, width: 740, maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
            <div style={mHead}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-color)' }}>Portal Preview</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>How this content looks embedded on the portal</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" style={ghostBtn} onClick={() => { navigator.clipboard.writeText(getHtml() || ''); }}>
                  Copy HTML
                </button>
                <button type="button" onClick={() => setPreviewOpen(false)} style={ghostBtn}>✕</button>
              </div>
            </div>
            <div className="re-editor" style={{ flex: 1, overflowY: 'auto', padding: '28px 36px' }}
              dangerouslySetInnerHTML={{ __html: getHtml() || '' }} />
          </div>
        </div>
      )}
    </>
  );
};

export default RichEditor;
